import type { QueueItem } from '@tabel/shared/schemas/agent-client';
import { ArrowUp, Square, TriangleAlert } from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ApiError } from '../../lib/errors';
import { ErrorCodes, errorCode, errorSentence } from '../ErrorToast';
import { Button } from '../ui';
import { COMPOSER_ATTR, RequestCard } from './RequestCard';
import { LABEL, PLACEHOLDER, QUEUE_LEAD } from './texts';
import type { RequestDecision, RequestRow } from './types';
import { useCoarsePointer, useKeyboardInset, usePhone } from './viewport';

/*
 * Поле сообщения (T074, T117; contracts/ui.md §2, §6; FR-037, FR-039, FR-040, FR-050; мокап Main.dc.html `.dock`).
 * - Над полем — очередь: «Отправится после ответа: «…» · Убрать» (начало строки — по причине очереди).
 * - Запрос агента заменяет поле карточкой (`RequestCard`); после ответа фокус — снова в поле: у поля атрибут
 *   `data-assistant-composer` (`COMPOSER_ATTR`), по нему карточка возвращает фокус.
 * - Под полем слева — выбор модели и кольцо «Память разговора»; справа — «Отправить» (главная). Пока агент работает —
 *   «Остановить агента», а с текстом в поле ещё «Отправить после ответа» и главная «Отправить сразу».
 * - Enter отправляет, Shift+Enter переносит строку; на сенсорном экране Enter всегда переносит строку (FR-039). Пока
 *   агент работает, Enter — «Отправить после ответа», как в мокапе.
 * - Ключ сообщения — новый на каждое сообщение; повтор того же текста после сбоя — с тем же ключом: сервер не примет
 *   сообщение дважды (FR-021).
 * - Ошибка ФИО (`PERSONAL_DATA`) — ошибкой поля с текстом сервера; другая ошибка — тоже под полем, с кодом ошибки.
 *   Текст остаётся в поле.
 * - На телефоне поле — 16 px и в одну строку до ввода; клавиатура не закрывает поле (`visualViewport`).
 */

/** Цель нажатия на телефоне — не ниже 40 px (на странице «Помощник»; в панели на весь экран это делает SidePanel). */
const TAP = 'max-sm:min-h-10';

export type ComposerMode = 'idle' | 'running' | 'disabled';
export type SendMode = 'turn' | 'steer' | 'queue';
export interface Outgoing {
  text: string;
  mode: SendMode;
  key: string;
}

/** Ключ сообщения: `[A-Za-z0-9_-]{8,64}`. */
export function newMessageKey(): string {
  const raw =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `m-${raw.replace(/[^A-Za-z0-9_-]/gu, '')}`.slice(0, 64);
}

interface FieldError {
  text: string;
  requestId: string | null;
  code: string | null;
}

function fieldError(e: unknown): FieldError {
  if (e instanceof ApiError && e.code === 'PERSONAL_DATA')
    return { text: e.message || errorSentence(e), requestId: null, code: null };
  return {
    text: errorSentence(e),
    requestId: e instanceof ApiError ? e.requestId : null,
    code: errorCode(e),
  };
}

export interface ComposerProps {
  mode: ComposerMode;
  /** Ждущий запрос агента с одноразовым номером: карточка вместо поля. */
  request: RequestRow | null;
  queue: readonly QueueItem[];
  /** Текст для поля («Спросить агента», карточка дня): `tick` меняется — поле заполняется. */
  draft: { text: string; tick: number } | null;
  stopping: boolean;
  /** Слева под полем: выбор модели и кольцо памяти. */
  left: ReactNode;
  onSend: (m: Outgoing) => Promise<void>;
  onStop: () => void;
  onRemove: (key: string) => void;
  onResolve: (row: RequestRow, decision: RequestDecision, answers?: string[]) => Promise<void>;
  layout: 'panel' | 'page';
  /** Поле сообщения — для фокуса при открытии панели. */
  inputRef?: RefObject<HTMLTextAreaElement | null>;
}

export function Composer(p: ComposerProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<FieldError | null>(null);
  const [sending, setSending] = useState(false);
  const own = useRef<HTMLTextAreaElement>(null);
  const field = p.inputRef ?? own;
  /** Последнее неотправленное сообщение и его ключ: повтор после сбоя — с тем же ключом. */
  const last = useRef<{ text: string; key: string } | null>(null);
  const coarse = useCoarsePointer();
  // На телефоне поле в одну строку: пример короче (мокап PhoneChat.dc.html).
  const phone = usePhone();
  const inset = useKeyboardInset();

  // Текст «Спросить агента» — в поле, с фокусом; сообщение не уходит само.
  const draftTick = p.draft?.tick;
  // biome-ignore lint/correctness/useExhaustiveDependencies: поле заполняется только при новом `tick`
  useEffect(() => {
    if (!p.draft) return;
    setText(p.draft.text);
    setError(null);
    const el = field.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const end = p.draft.text.length;
    el.setSelectionRange(end, end);
  }, [draftTick]);

  // Высота поля — по тексту, до 160 px.
  // biome-ignore lint/correctness/useExhaustiveDependencies: высота зависит от текста
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    if (el.scrollHeight > 0) el.style.height = `${Math.min(160, el.scrollHeight)}px`;
    // Полоса прокрутки — только когда текст выше 160 px (пустое поле с длинным примером её не показывает).
    el.style.overflowY = text !== '' && el.scrollHeight > 160 ? 'auto' : 'hidden';
  }, [text]);

  const running = p.mode === 'running';
  const disabled = p.mode === 'disabled';
  const hasText = text.trim().length > 0;

  const submit = async (mode: SendMode) => {
    const t = text.trim();
    if (!t || sending || disabled) return;
    const key = last.current?.text === t ? last.current.key : newMessageKey();
    last.current = { text: t, key };
    setSending(true);
    try {
      await p.onSend({ text: t, mode, key });
      last.current = null;
      setText('');
      setError(null);
    } catch (e) {
      setError(fieldError(e));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || coarse) return;
    e.preventDefault();
    void submit(running ? 'queue' : 'turn');
  };

  const queue = p.queue.filter((q) => q.reason !== 'retry');
  const col = p.layout === 'page' ? 'mx-auto w-full max-w-[760px]' : 'w-full';
  const errorId = 'assistant-field-error';

  return (
    <div
      data-composer
      className={`flex flex-col gap-2 ${col}`}
      style={inset > 0 ? { paddingBottom: inset } : undefined}
    >
      {queue.map((q) => (
        <div
          key={q.key}
          data-queue
          className="flex items-center gap-2 rounded-control bg-subtle py-1.5 pr-1.5 pl-3 text-[13px] text-ink-2"
        >
          <span className="shrink-0">{QUEUE_LEAD[q.reason ?? 'none'] ?? QUEUE_LEAD.none}</span>
          <span className="min-w-0 flex-1 truncate text-ink">{q.text}</span>
          <Button size="sm" className={TAP} variant="ghost" onClick={() => p.onRemove(q.key)}>
            {LABEL.remove}
          </Button>
        </div>
      ))}
      {p.request ? (
        // Фокус после ответа карточка возвращает сама — в поле с `COMPOSER_ATTR`.
        <RequestCard
          row={p.request}
          onResolve={async (decision, answers) => {
            const row = p.request;
            if (row) await p.onResolve(row, decision, answers);
          }}
        />
      ) : (
        <>
          <div
            className={`rounded-control border bg-surface transition-[border-color,box-shadow] duration-150 focus-within:border-accent focus-within:shadow-focus-field motion-reduce:transition-none ${error ? 'border-bad' : 'border-line hover:border-line-strong'}`}
          >
            <textarea
              ref={field}
              {...{ [COMPOSER_ATTR]: '' }}
              rows={1}
              value={text}
              disabled={disabled}
              aria-label={LABEL.message}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              placeholder={
                phone
                  ? running
                    ? PLACEHOLDER.runningShort
                    : PLACEHOLDER.idleShort
                  : running
                    ? PLACEHOLDER.running
                    : PLACEHOLDER.idle
              }
              onChange={(e) => {
                setText(e.target.value);
                if (error) setError(null);
              }}
              onKeyDown={onKeyDown}
              className="block max-h-40 min-h-9 w-full resize-none border-0 bg-transparent px-3 pt-2 pb-1 text-[14px] leading-[1.5] text-ink placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed max-sm:text-base"
            />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1.5 pt-0.5 pb-1.5">
              <div className="flex min-w-0 items-center gap-1">{p.left}</div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {running && (
                  <Button size="sm" className={TAP} icon={Square} disabled={p.stopping} onClick={p.onStop}>
                    {p.stopping ? LABEL.stopping : LABEL.stop}
                  </Button>
                )}
                {running && hasText && (
                  <>
                    <Button size="sm" className={TAP} disabled={sending} onClick={() => void submit('queue')}>
                      {LABEL.sendAfter}
                    </Button>
                    <Button
                      size="sm"
                      className={TAP}
                      variant="primary"
                      disabled={sending}
                      onClick={() => void submit('steer')}
                    >
                      {LABEL.sendNow}
                    </Button>
                  </>
                )}
                {!running && (
                  <Button
                    size="sm"
                    className={TAP}
                    variant="primary"
                    icon={ArrowUp}
                    disabled={!hasText || sending || disabled}
                    onClick={() => void submit('turn')}
                  >
                    {LABEL.send}
                  </Button>
                )}
              </div>
            </div>
          </div>
          {error && (
            <p
              id={errorId}
              role="alert"
              className="flex items-start gap-1.5 text-[12px] leading-[1.5] text-bad"
            >
              <TriangleAlert size={16} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
              <span className="min-w-0">
                {error.text}
                {(error.requestId || error.code) && (
                  <span className="block text-ink-2">
                    {LABEL.errorCode} <ErrorCodes requestId={error.requestId} code={error.code} />
                  </span>
                )}
              </span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
